import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import { Role } from '@/constants/rolePermissions';
import { useUserManagement } from '@/hooks/useUserManagement';
import {
  createUserSchema,
  CreateUserSchema,
} from '@/schemas/admin/UserManagementTabs/createUserSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (createdUserId?: string) => void;
  userRole: Role;
}

const CreateUserModal = ({
  isOpen,
  onClose,
  onSuccess,
  userRole,
}: CreateUserModalProps) => {
  const { createUser, loading: isCreatingUser } = useUserManagement();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<CreateUserSchema>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    },
    mode: 'onChange',
  });

  const handleClose = () => {
    form.reset();
    setServerError(null);
    onClose();
  };

  const onSubmit = async (values: CreateUserSchema) => {
    setServerError(null);

    const { error, data } = await createUser({
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      phone: values.phone,
      role: userRole,
    });

    if (!error) {
      form.reset();

      if (
        userRole === 'property_owner' ||
        userRole === 'property_manager' ||
        userRole === 'cleaning'
      ) {
        onSuccess(data?.user_id);
      } else {
        onSuccess();
      }

      onClose();
    } else {
      setServerError(error.message || 'No se pudo crear el usuario');

      setTimeout(() => {
        handleClose();
      }, 5000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => open || handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Crear {userRole === 'super_admin' ? 'Administrador' : 'Propietario'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre</FormLabel>
                    <FormControl>
                      <Input placeholder="Ej. Juan" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apellidos</FormLabel>
                    <FormControl>
                      <Input placeholder="Ej. Pérez López" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Correo electrónico</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Ej. juan.perez@email.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Teléfono</FormLabel>
                  <FormControl>
                    <PhoneInput
                      placeholder="+52 333 123 4567"
                      maxLength={16}
                      value={field.value || ''}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {serverError && (
              <p className="text-sm text-red-600 -mt-2">{serverError}</p>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancelar
              </Button>

              <Button
                type="submit"
                className="bg-brand-primary hover:bg-brand-primary-hover"
                disabled={isCreatingUser || !form.formState.isValid}
              >
                {isCreatingUser ? 'Creando...' : 'Crear Usuario'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;
